import { DeleteOutlined, RefreshOutlined } from "@mui/icons-material";
import { Box, CircularProgress, IconButton, Typography } from "@mui/material";
import { DateTime } from "luxon";
import { type ChangeEvent, type FC, useState } from "react";
import { useAuth } from "../../contexts/AuthProvider";
import { useErrorSnackbar } from "../../hooks/useErrorSnackbar";
import { SkeletonWithRandomWidths } from "../../pages/Profile/v2/util/constants";
import { INK, STATUS, STATUS_BADGE } from "../../theme/tokens";
import { MAX_TIME_OFF_SPAN_DAYS } from "../../utils/consts";
import { formatWeekRangeWithMonth } from "../../utils/dateFormatting";
import { type TimeOffRequest, TimeOffRequestReason, TimeOffRequestStatus } from "../../utils/models";
import { SystemDropdown } from "../UIToolkit/Inputs/SystemDropDown";
import { SystemValidationTextField } from "../UIToolkit/Inputs/SystemValidationTextField";
import { DateRangeSelector } from "../UIToolkit/molecules/DateRangeSelector";
import { VirtualizedList } from "../UIToolkit/molecules/VirtualizedList";
import SystemDialog from "../UIToolkit/Popups/Dialog";
import { InHouseMobileSheet } from "../UIToolkit/Popups/InHouseMobileSheet";
import SystemButton from "../UIToolkit/SystemButton/SystemButton";
import { useCancelTimeOffRequest, useCreateTimeOffRequest, useGetMyTimeOffRequests } from "./timeOffRequestsHooks";

type Props = {
	open: boolean;
	onClose: () => void;
};

const getStatusColor = (status: TimeOffRequestStatus) => {
	switch (status) {
		case "pending":
			return STATUS_BADGE.AMBER_INK;
		case "approved":
			return STATUS.SUCCESS.main;
		case "declined":
			return STATUS.ERROR.main;
		default:
			return INK.MUTED;
	}
};

const ExistingTimeOffList = () => {
	const { data, isLoading } = useGetMyTimeOffRequests();
	const { mutateAsync: cancelRequest, isPending: cancelRequestLoading } = useCancelTimeOffRequest();
	const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
	const [selectedRequestId, setSelectedRequestId] = useState<string | null>(null);

	const sortedData = data?.sort((a, b) => b.endDate.diff(a.startDate, "days").days);

	const handleCancelClick = (requestId: string) => {
		setSelectedRequestId(requestId);
		setConfirmDialogOpen(true);
	};

	const handleConfirmCancel = async () => {
		if (!selectedRequestId) return;
		try {
			await cancelRequest(selectedRequestId);
			setConfirmDialogOpen(false);
			setSelectedRequestId(null);
		} catch {
			// Error handled in hook
		}
	};

	const handleCloseDialog = () => {
		setConfirmDialogOpen(false);
		setSelectedRequestId(null);
	};

	return (
		<>
			{sortedData && !isLoading ? (
				<VirtualizedList
					items={sortedData}
					rowHeight={40}
					renderItem={(item: TimeOffRequest) => {
						const isLoadingDelete = cancelRequestLoading && selectedRequestId === item.id;
						const status = item.status;
						return (
							<Box
								key={item.id}
								sx={{
									display: "flex",
									gap: 1,
									alignItems: "center",
									justifyContent: "space-between",
									width: "100%",
								}}
							>
								<Box
									sx={{
										display: "flex",
										gap: 1,
										alignItems: "center",
										justifyContent: "start",
										fontSize: "14px",
									}}
								>
									<Typography
										sx={{
											whiteSpace: "nowrap",
										}}
									>
										{formatWeekRangeWithMonth(item.startDate, item.endDate, true, true)}
									</Typography>
									<Typography
										sx={{
											maxWidth: { xs: "120px", mediumPhone: "180px", sm: "400px" },
											overflow: "hidden",
											textOverflow: "ellipsis",
											whiteSpace: "nowrap",
											display: "block",
										}}
									>
										{item.reason !== TimeOffRequestReason.Other ? item.reason : item.reasonExplanation}
									</Typography>
								</Box>
								<Box
									sx={{
										display: "flex",
										gap: 0.25,
										alignItems: "center",
									}}
								>
									<Typography
										sx={{
											color: getStatusColor(status),
										}}
									>
										{status}
									</Typography>

									{status === TimeOffRequestStatus.PENDING ? (
										isLoadingDelete ? (
											<CircularProgress size={"14px"} />
										) : (
											<IconButton size="small" onClick={() => handleCancelClick(item.id)}>
												<DeleteOutlined fontSize="small" />
											</IconButton>
										)
									) : null}
								</Box>
							</Box>
						);
					}}
				/>
			) : (
				<SkeletonWithRandomWidths lines={4} />
			)}
			<SystemDialog
				open={confirmDialogOpen}
				onClose={handleCloseDialog}
				title="Cancel Time Off Request"
				body="Are you sure you want to cancel this time off request?"
				cancel="No, Keep It"
				confirm="Yes, Cancel Request"
				onConfirm={handleConfirmCancel}
				confirmDisabled={cancelRequestLoading}
			/>
		</>
	);
};

export const RequestTimeOff: FC<Props> = ({ open, onClose }) => {
	const showErrorSnackbar = useErrorSnackbar();
	const today = DateTime.now().startOf("day");
	const { userInfo } = useAuth();
	const [startDate, setStartDate] = useState<DateTime>(today);
	const [endDate, setEndDate] = useState<DateTime | undefined>();
	const [reason, setReason] = useState<TimeOffRequestReason | undefined>();
	const [extraDetails, setExtraDetails] = useState<string>("");
	const nurseId = userInfo?.nurses_info?.at(0)?.nurse.id;
	const { mutateAsync: performCreateTimeOffRequest, isPending: createTimeOffLoading } = useCreateTimeOffRequest();

	const handleStartDateChange = (date: DateTime) => {
		setStartDate(date);
		// endMaxDate only constrains future picks — realign an already-picked end
		// so moving the start earlier can't leave an over-cap span.
		const maxEnd = date.plus({ days: MAX_TIME_OFF_SPAN_DAYS });
		if (endDate && endDate > maxEnd) {
			setEndDate(maxEnd);
		}
	};
	const handleEndDateChange = (date: DateTime) => {
		setEndDate(date);
	};

	const resetState = () => {
		setStartDate(today);
		setEndDate(undefined);
		setReason(undefined);
		setExtraDetails("");
	};

	const handleClose = () => {
		resetState();
		onClose();
	};

	const onInputChange = (event: ChangeEvent<HTMLInputElement>) => {
		setExtraDetails(event.target.value);
	};

	const getSubmitData = () => {
		if (!startDate || !endDate || !reason) return null;
		if (reason === TimeOffRequestReason.Other && extraDetails.trim() === "") return null;
		return { startDate, endDate, reason };
	};
	const isSaveDisabled = !getSubmitData();

	const handleSubmitRequest = async () => {
		const submitData = getSubmitData();
		if (!submitData) {
			return;
		}
		if (!nurseId) {
			showErrorSnackbar("Error trying to performCreateTimeOffRequest - no nurse ID.", {});
			return;
		}
		const startDateIso = submitData.startDate.toISODate();
		const endDateIso = submitData.endDate.toISODate();
		if (!startDateIso || !endDateIso) {
			showErrorSnackbar("Invalid time off request dates.", {});
			return;
		}
		try {
			await performCreateTimeOffRequest({
				startDate: startDateIso,
				endDate: endDateIso,
				nurseId,
				reason: submitData.reason,
				reasonExplanation: extraDetails,
			});
			resetState();
		} catch {
			//handled in hook
		}
	};

	return (
		<InHouseMobileSheet onClose={handleClose} open={open}>
			<Box
				sx={{
					display: "flex",
					flexDirection: "column",
					justifyContent: "space-between",
					height: "100%",
				}}
			>
				<Box
					sx={{
						display: "flex",
						flexDirection: "column",
						gap: 2,
					}}
				>
					<Box
						sx={{
							display: "flex",
							alignItems: "center",
							gap: 1,
							justifyContent: "space-between",
						}}
					>
						<Typography
							sx={{
								fontSize: 18,
								fontWeight: 600,
							}}
						>
							Create time off request
						</Typography>
						<SystemButton leadingIcon={<RefreshOutlined />} onClick={resetState} buttonVariant={"borderless"} noFullWidthMobile={true}>
							Reset form
						</SystemButton>
					</Box>
					<DateRangeSelector
						startDate={startDate}
						minDate={today}
						endDate={endDate}
						endMaxDate={startDate.plus({ days: MAX_TIME_OFF_SPAN_DAYS })}
						onStartDateChange={handleStartDateChange}
						onEndDateChange={handleEndDateChange}
					/>
					<SystemDropdown label={"Reason"} items={Object.values(TimeOffRequestReason)} selectedItem={reason} onChange={setReason} fullWidth />
					{reason === TimeOffRequestReason.Other && <SystemValidationTextField label={"Please specify"} value={extraDetails} onChange={onInputChange} />}
				</Box>

				<Box
					sx={{
						flex: 1,
						py: 1,
						maxHeight: "auto",
						overflow: "auto",
						position: "relative",

						maskImage: `
						  linear-gradient(to bottom,
							transparent,
							black 12px,
							black calc(100% - 12px),
							transparent
						  )
						`,

						WebkitMaskImage: `
						  linear-gradient(to bottom,
							transparent,
							black 12px,
							black calc(100% - 12px),
							transparent
						  )
						`,
					}}
				>
					<ExistingTimeOffList />
				</Box>

				<Box
					sx={{
						display: "flex",
						justifyContent: "flex-end",
						alignItems: "center",
						gap: 2,
					}}
				>
					<SystemButton buttonVariant={"secondary"} onClick={handleClose}>
						Cancel
					</SystemButton>
					<SystemButton loading={createTimeOffLoading} disabled={isSaveDisabled} onClick={handleSubmitRequest}>
						Submit Request
					</SystemButton>
				</Box>
			</Box>
		</InHouseMobileSheet>
	);
};
